import { useClientStore } from '@/stores/client'
import { useCoreSocket } from '@/services/socket/core-socket'
import { useOfficeStore } from '@/stores/office'
import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router'
import * as Sentry from '@sentry/vue'

interface PostMessageData {
  type?: string
  entry?: string | Record<string, unknown>
  meta?: string | Record<string, unknown>
  source?: string
  __NG_DEVTOOLS_EVENT__?: boolean
}

interface OfficeMeta {
  userId?: string
}

const socket = useCoreSocket()

export const officeGuard = async (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
): Promise<void> => {
  const clientStore = useClientStore()
  const officeStore = useOfficeStore()
  const controller = new AbortController()

  if (clientStore.authenticated && clientStore.user) {
    if (!officeStore.getOffice) {
      window.addEventListener(
        'message',
        (e) => {
          const data = e.data as PostMessageData

          // Ignore non-object messages
          if (!data || typeof data !== 'object') return

          // Ignore common devtools/extension messages
          if (
            data.__NG_DEVTOOLS_EVENT__ === true ||
            (typeof data.source === 'string' && data.source.includes('devtools'))
          ) {
            return
          }

          const type = data?.type
          const allowedTypes = new Set(['inject_data', 'default_data', 'init_success'])

          // Silently ignore any messages we don't recognize
          if (!type || !allowedTypes.has(type)) return

          const expectedOrigin = clientStore.getUser?.origin

          if (type === 'inject_data') {
            if (e.origin !== expectedOrigin) return

            let entry: Record<string, unknown> | null = null
            let meta: OfficeMeta | null = null
            try {
              entry =
                typeof data?.entry === 'string'
                  ? JSON.parse(data.entry)
                  : ((data?.entry as Record<string, unknown>) ?? null)
              meta = typeof data?.meta === 'string' ? JSON.parse(data.meta) : ((data?.meta as OfficeMeta) ?? null)
            } catch (err) {
              Sentry.logger.warn('Ignoring malformed inject_data payload (office)', { error: String(err) })
              return
            }
            if (meta && meta.userId && entry) {
              // Got existing Office from client
              socket?.messageCenter?.triggerMessage('validate_injected_data', { meta, architect_office: entry })
              controller.abort()
              Sentry.metrics.count('widget_event', 1, { attributes: { event_type: type, context: 'office' } })
              Sentry.logger.info('Widget event', { event_type: type, context: 'office', userId: meta.userId })
            }
            return
          }

          if (type === 'default_data') {
            if (e.origin !== expectedOrigin) return

            let meta: OfficeMeta | null = null
            try {
              meta = typeof data?.meta === 'string' ? JSON.parse(data.meta) : ((data?.meta as OfficeMeta) ?? null)
            } catch (err) {
              Sentry.logger.warn('Ignoring malformed default_data payload (office)', { error: String(err) })
              return
            }
            if (meta && meta.userId) {
              /* Got request for default Office from client */
              socket?.messageCenter?.triggerMessage('get_default_data', { meta })
              controller.abort()
              Sentry.metrics.count('widget_event', 1, { attributes: { event_type: type, context: 'office' } })
              Sentry.logger.info('Widget event', { event_type: type, context: 'office', userId: meta.userId })
            }
            return
          }

          if (type === 'init_success') {
            if (clientStore.standalone) {
              Sentry.logger.debug('Standalone office mode active (development only)')
              const meta = { userId: 'a1000000-0000-0000-0000-000000000000', title: 'Widget Test Office' }
              socket?.messageCenter?.triggerMessage('get_default_data', { meta })
              controller.abort()
              Sentry.metrics.count('widget_event', 1, { attributes: { event_type: type, context: 'office' } })
              Sentry.logger.info('Widget event', { event_type: type, context: 'office', userId: meta.userId })
            }
            // Not standalone: ignore init_success from outside
            return
          }
        },
        { signal: controller.signal }
      )

      const socketDataReady = await socket.checkSocketDataReady()
      if (socketDataReady) {
        next()
      } else {
        next({ name: 'error', params: { key: 'errors.general_error' } })
      }
    } else {
      next()
    }
  } else {
    next({ name: 'error', params: { key: 'errors.unknown_error' } })
  }
}
